import {
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/projects/IPO/components/ui/table";
import { ChevronDown, ChevronRight } from "lucide-react";

interface FinancialTableProps {
  data: any[];
  title: string;
  expandedRows: Record<string, boolean>;
  onToggleRow: (title: string, columnName: string) => void;
  viewType?: string;
}

const FinancialTable = ({
  data,
  title,
  expandedRows,
  onToggleRow,
  viewType,
}: FinancialTableProps) => {
  // Helper function to extract and sort year columns
  const getYearColumns = (data: any[]): string[] => {
    if (!data || data.length === 0) {
      return [];
    }

    const yearKeys = new Set<string>();
    data.forEach(row => {
      Object.keys(row).forEach(key => {
        if (key.startsWith("Y") && key.length === 7) {
          yearKeys.add(key);
        }
      });
    });

    // If no Y items found, try to use other keys excluding known non-data keys
    // This provides fallback compatibility for data that might not strictly follow Y-prefix
    if (yearKeys.size === 0 && data.length > 0) {
      Object.keys(data[0])
        .filter(
          k =>
            k !== "COLUMNNAME" &&
            k !== "rowno" &&
            k !== "RID" &&
            k !== "indentLevel"
        )
        .forEach(k => yearKeys.add(k));

      // If we fell back to generic keys, we might not want to sort them by date logic if they don't match
      // But for now let's assume if they exist they are sortable or just return as is
      // However, the original code for generic table just filtered and mapped.
      // Let's rely on getYearColumns logic being primarily for Y-keys.
      // If Y-keys are found, use them. If not, fallback to Object.keys but be careful about sorting.
    }

    const keysArray = Array.from(yearKeys);

    // Attempt to sort if they look like Y dates
    const isYDate = keysArray.every(k => k.startsWith("Y") && k.length === 7);

    if (isYDate) {
      return keysArray.sort((a, b) => {
        const yearA = parseInt(a.substring(1, 5));
        const quarterA = parseInt(a.substring(5, 7));
        const yearB = parseInt(b.substring(1, 5));
        const quarterB = parseInt(b.substring(5, 7));

        if (yearB !== yearA) {
          return yearB - yearA;
        }
        return quarterB - quarterA;
      });
    }

    return keysArray;
  };

  // Helper function to format year key to display format
  const formatYearKey = (key: string): string => {
    if (key.startsWith("Y") && key.length === 7) {
      const year = key.substring(1, 5);
      const month = key.substring(5, 7);
      return `${month} ${year}`;
    }
    // Fallback for generic formatting if needed, matching the generic table logic
    // .replace("Y", "").replace(/(\d{4})(\d{2})/, "$2 $1")
    return key.replace("Y", "").replace(/(\d{4})(\d{2})/, "$2 $1");
  };

  // Helper function to check if a row has children
  const hasChildren = (data: any[], index: number): boolean => {
    if (!data || index >= data.length - 1) {
      return false;
    }
    const currentRow = data[index];
    const nextRow = data[index + 1];
    if (!currentRow || !nextRow) {
      return false;
    }
    const currentLevel = currentRow.indentLevel ?? 0;
    const nextLevel = nextRow.indentLevel ?? 0;
    return nextLevel > currentLevel;
  };

  // Helper function to check if a row should be visible
  const isRowVisible = (
    data: any[],
    index: number,
    sectionTitle: string
  ): boolean => {
    if (index === 0) {
      return true;
    }
    const row = data[index];
    if (!row) {
      return false;
    }
    const indentLevel = row.indentLevel ?? 0;

    // Check all parent rows are expanded
    for (let i = index - 1; i >= 0; i--) {
      const prevRow = data[i];
      if (!prevRow) {
        break;
      }
      const prevLevel = prevRow.indentLevel ?? 0;

      if (prevLevel < indentLevel) {
        // This is a parent row
        const parentKey = `${sectionTitle}-${prevRow.COLUMNNAME}`;
        if (!expandedRows[parentKey]) {
          return false;
        }
        // Found immediate parent, stop checking
        break;
      }
    }

    return true;
  };

  if (!data || data.length === 0) {
    return (
      <div className="flex min-h-[150px] w-full items-center justify-center">
        <span className="text-gray-400">
          No data available {viewType ? `(${viewType})` : ""}
        </span>
      </div>
    );
  }

  const columns = getYearColumns(data).slice(0, 10);

  return (
    <div className="relative max-h-[580px] w-full overflow-auto">
      <table className="relative table w-full min-w-full caption-bottom text-sm md:table-auto">
        <TableHeader className="sticky top-0 z-40 bg-gray-50">
          <TableRow>
            <TableHead className="sticky left-0 z-50 w-[280px] border-r border-gray-100 bg-gray-50 py-4 pl-6 text-xs font-bold text-gray-500 shadow-[2px_0_4px_rgba(0,0,0,0.05)]">
              Particulars
            </TableHead>
            {columns.map(key => (
              <TableHead
                key={key}
                className="min-w-[120px] bg-gray-50 py-4 text-right text-xs font-bold text-gray-900"
              >
                {formatYearKey(key)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row: any, rIdx: number) => {
            if (!isRowVisible(data, rIdx, title)) {
              return null;
            }
            const canExpand = hasChildren(data, rIdx);
            const isExpanded = expandedRows[`${title}-${row.COLUMNNAME}`];

            return (
              <TableRow
                key={`${title}-${row.COLUMNNAME}-${row.RID ?? rIdx}`}
                className={`group border-gray-50 transition-colors hover:bg-gray-100/50 ${
                  rIdx % 2 === 0 ? "bg-white" : "bg-gray-50"
                }`}
              >
                <TableCell
                  className={`sticky left-0 z-30 cursor-pointer whitespace-nowrap border-r border-gray-100/50 py-4 pl-6 text-sm font-medium text-gray-600 shadow-[2px_0_4px_rgba(0,0,0,0.05)] ${
                    rIdx % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
                  onClick={() => {
                    if (canExpand) {
                      onToggleRow(title, row.COLUMNNAME);
                    }
                  }}
                >
                  <div
                    style={{
                      paddingLeft: `${(row.indentLevel ?? 0) * 1.5}rem`,
                    }}
                    className="flex items-center gap-2"
                  >
                    {canExpand ? (
                      isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-[#00D09C]" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      )
                    ) : row.indentLevel ? (
                      <div className="ml-1 mr-1.5 h-1.5 w-1.5 rounded-full bg-gray-300" />
                    ) : (
                      <div className="w-4" />
                    )}
                    <span
                      className={canExpand ? "font-bold text-gray-900" : ""}
                    >
                      {row.COLUMNNAME}
                    </span>
                  </div>
                </TableCell>
                {columns.map((key, cIdx) => (
                  <TableCell
                    key={key}
                    className={`py-4 text-right text-sm font-medium text-gray-900 ${
                      cIdx % 2 === 0 ? "bg-black/[0.02]" : ""
                    }`}
                  >
                    {typeof row[key] === "number"
                      ? (row[key] as number).toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2,
                        })
                      : (row[key] ?? "—")}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </table>
    </div>
  );
};

export default FinancialTable;
